import { FC } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { Userbot } from "../../interfaces"
import { formatPhone, shortId } from "./identityFormat"
import { IdentityTooltip } from "./identityTooltip"

export interface AccountIdentityProps {
  account: Userbot.Account | Userbot.AccountFeedItem | null | undefined
  compact?: boolean
  showTooltip?: boolean
}

export const AccountIdentity: FC<AccountIdentityProps> = ({
  account,
  compact = false,
  showTooltip = true,
}) => {
  if (!account) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const isPending =
    account.id === null ||
    ("pendingLogin" in account && account.pendingLogin === true)

  if (isPending) {
    const element = (
      <Typography
        variant="body2"
        color="warning.main"
        fontWeight={compact ? 400 : 500}
        noWrap={compact}
      >
        Ожидает входа
      </Typography>
    )
    return showTooltip ? (
      <IdentityTooltip account={account}>{element}</IdentityTooltip>
    ) : (
      element
    )
  }

  const tgId = account.telegramUserId
  const formattedPhone = formatPhone(account.phone)
  const fullName = [account.firstName, account.lastName]
    .filter(Boolean)
    .join(" ")
  const username = account.username ? `@${account.username}` : ""
  const primaryText = tgId || formattedPhone || shortId(account.id)

  if (compact) {
    let details = ""
    if (fullName && username) {
      details = `${fullName} (${username})`
    } else if (fullName) {
      details = fullName
    } else if (username) {
      details = username
    } else if (formattedPhone && tgId) {
      details = formattedPhone
    }

    const label = details ? `${primaryText} · ${details}` : primaryText
    const element = (
      <Typography
        variant="body2"
        noWrap
        sx={{
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
    )
    return showTooltip ? (
      <IdentityTooltip account={account}>{element}</IdentityTooltip>
    ) : (
      element
    )
  }

  const secondaryDetails = tgId
    ? [username, formattedPhone].filter(Boolean).join(" · ")
    : username

  const content = (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontWeight: 500 }}
        noWrap
      >
        {primaryText}
      </Typography>
      {fullName ? (
        <Typography variant="caption" color="text.secondary" noWrap>
          {fullName}
        </Typography>
      ) : null}
      {secondaryDetails ? (
        <Typography variant="caption" color="text.secondary" noWrap>
          {secondaryDetails}
        </Typography>
      ) : null}
    </Stack>
  )

  return showTooltip ? (
    <IdentityTooltip account={account}>
      <Box sx={{ display: "inline-block", minWidth: 0 }}>{content}</Box>
    </IdentityTooltip>
  ) : (
    content
  )
}
